import { formatPrice } from '@kitup/brand';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent, type JSX } from 'react';
import { Link, useSearchParams } from 'react-router';
import { adminPromotionsApi, type CreateAdminPromotionInput } from '../api/admin-promotions.js';
import { useAuth } from '../auth/context.js';
import { useBrand } from '../brand/context.js';
import { useI18n } from '../i18n/context.js';

export function AdminPromotionsPage(): JSX.Element {
  const { user } = useAuth();
  const { t, locale, formatDate } = useI18n();
  const brand = useBrand();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const requestedEmail = searchParams.get('email') ?? '';
  const [showForm, setShowForm] = useState(Boolean(requestedEmail));
  const [form, setForm] = useState(() => emptyForm(requestedEmail));
  const promotions = useQuery({
    queryKey: ['admin-promotions'],
    queryFn: adminPromotionsApi.list,
    enabled: user?.role === 'admin',
  });
  const create = useMutation({
    mutationFn: adminPromotionsApi.create,
    onSuccess: () => {
      setForm(emptyForm());
      setShowForm(false);
      void queryClient.invalidateQueries({ queryKey: ['admin-promotions'] });
    },
  });
  const toggle = useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) =>
      adminPromotionsApi.setActive(id, active),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['admin-promotions'] }),
  });

  if (user?.role !== 'admin') {
    return (
      <main className="mx-auto max-w-3xl p-6">
        <p role="alert">{t.admin.forbidden}</p>
        <Link to="/app" className="kitup-quiet-button mt-4">
          {t.admin.toApp}
        </Link>
      </main>
    );
  }

  const submit = (event: FormEvent): void => {
    event.preventDefault();
    if (form.discountPercent === 100 && !window.confirm(t.admin.promotions.freeConfirm)) return;
    create.mutate(toInput(form));
  };
  const preview = (amount: number): string =>
    formatPrice(Math.round((amount * (100 - form.discountPercent)) / 100), brand, locale);

  return (
    <main className="mx-auto max-w-6xl p-4 sm:p-6">
      <header className="mb-6 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">{t.admin.promotions.title}</h1>
          <p className="text-sm text-[var(--kitup-color-muted)]">{t.admin.promotions.subtitle}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            className="kitup-primary-button"
            onClick={() => setShowForm((value) => !value)}
          >
            {showForm ? t.common.close : t.admin.promotions.create}
          </button>
          <Link to="/admin/accounts" className="kitup-quiet-button">
            {t.admin.accounts.title}
          </Link>
          <Link to="/admin/usage" className="kitup-quiet-button">
            {t.admin.usage.title}
          </Link>
          <Link to="/app" className="kitup-quiet-button">
            {t.admin.toApp}
          </Link>
        </div>
      </header>

      {showForm ? (
        <form className="kitup-card mb-6 grid gap-4 p-5" onSubmit={submit}>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label={t.admin.promotions.name}>
              <input
                required
                maxLength={120}
                value={form.name}
                onChange={(event) => setForm({ ...form, name: event.target.value })}
              />
            </Field>
            <Field label={t.admin.promotions.code}>
              <div className="flex gap-2">
                <input
                  required
                  minLength={2}
                  maxLength={32}
                  pattern="[A-Za-z0-9][A-Za-z0-9-]+"
                  value={form.code}
                  onChange={(event) => setForm({ ...form, code: event.target.value.toUpperCase() })}
                />
                <button
                  type="button"
                  className="kitup-quiet-button"
                  onClick={() => setForm({ ...form, code: generateCode() })}
                >
                  {t.admin.promotions.generate}
                </button>
              </div>
            </Field>
            <Field label={t.admin.promotions.discount}>
              <input
                required
                type="number"
                min={0}
                max={100}
                step={1}
                value={form.discountPercent}
                onChange={(event) =>
                  setForm({ ...form, discountPercent: Number(event.target.value) })
                }
              />
            </Field>
            <Field label={t.admin.promotions.targetEmail}>
              <input
                type="email"
                placeholder={t.admin.promotions.anyUser}
                value={form.targetEmail}
                onChange={(event) => setForm({ ...form, targetEmail: event.target.value })}
              />
            </Field>
            <Field label={t.admin.promotions.maxTotal}>
              <input
                type="number"
                min={1}
                placeholder={t.admin.promotions.unlimited}
                value={form.maxRedemptions}
                onChange={(event) => setForm({ ...form, maxRedemptions: event.target.value })}
              />
            </Field>
            <Field label={t.admin.promotions.maxPerUser}>
              <input
                type="number"
                min={1}
                placeholder={t.admin.promotions.unlimited}
                value={form.maxPerUser}
                onChange={(event) => setForm({ ...form, maxPerUser: event.target.value })}
              />
            </Field>
            <Field label={t.admin.promotions.startsAt}>
              <input
                required
                type="datetime-local"
                value={form.startsAt}
                onChange={(event) => setForm({ ...form, startsAt: event.target.value })}
              />
            </Field>
            <Field label={t.admin.promotions.endsAt}>
              <input
                type="datetime-local"
                value={form.endsAt}
                onChange={(event) => setForm({ ...form, endsAt: event.target.value })}
              />
            </Field>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={form.firstPurchaseOnly}
              onChange={(event) => setForm({ ...form, firstPurchaseOnly: event.target.checked })}
            />
            {t.admin.promotions.firstPurchase}
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={form.active}
              onChange={(event) => setForm({ ...form, active: event.target.checked })}
            />
            {t.admin.promotions.activateImmediately}
          </label>
          <Field label={t.admin.promotions.note}>
            <textarea
              rows={2}
              maxLength={500}
              value={form.note}
              onChange={(event) => setForm({ ...form, note: event.target.value })}
            />
          </Field>
          <section className="rounded-lg bg-[var(--kitup-color-paper)] p-4 text-sm">
            <p className="font-semibold">{t.admin.promotions.preview}</p>
            {(['book', 'live_book', 'gift_book'] as const).map((plan) => (
              <p key={plan} className="mt-1 flex justify-between gap-4">
                <span>{t.monetization.plans[plan].name}</span>
                <span>
                  {formatPrice(brand.planPrices[plan], brand, locale)} →{' '}
                  <b>{preview(brand.planPrices[plan])}</b>
                </span>
              </p>
            ))}
            {form.discountPercent >= 50 ? (
              <p className="mt-3 text-amber-800">
                {form.discountPercent === 100
                  ? t.admin.promotions.freeWarning
                  : t.admin.promotions.largeWarning}
              </p>
            ) : null}
          </section>
          {create.isError ? (
            <p role="alert" className="text-sm text-red-700">
              {t.admin.promotions.createError}
            </p>
          ) : null}
          <button
            type="submit"
            className="kitup-primary-button justify-self-start"
            disabled={create.isPending}
          >
            {create.isPending ? t.admin.promotions.saving : t.admin.promotions.save}
          </button>
        </form>
      ) : null}

      {promotions.isPending ? <p>{t.common.loading}</p> : null}
      {promotions.isError ? <p role="alert">{t.common.unexpectedError}</p> : null}
      <div className="grid gap-3">
        {promotions.data?.items.map((promotion) => (
          <article key={promotion.id} className="kitup-card p-5">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <div className="flex flex-wrap items-center gap-2">
                  <code className="rounded bg-[var(--kitup-color-paper)] px-2 py-1 text-base font-semibold">
                    {promotion.code}
                  </code>
                  <strong>{promotion.discountPercent}%</strong>
                  <span
                    className={
                      promotionIsLive(promotion)
                        ? 'text-emerald-700'
                        : 'text-[var(--kitup-color-muted)]'
                    }
                  >
                    {!promotion.active
                      ? t.admin.promotions.paused
                      : new Date(promotion.startsAt).getTime() > Date.now()
                        ? t.admin.promotions.scheduled
                        : promotion.endsAt && new Date(promotion.endsAt).getTime() <= Date.now()
                          ? t.admin.promotions.ended
                          : t.admin.promotions.active}
                  </span>
                </div>
                <h2 className="mt-2 font-semibold">{promotion.name}</h2>
                <p className="mt-1 text-sm text-[var(--kitup-color-muted)]">
                  {promotion.targetEmail ?? t.admin.promotions.anyUser} ·{' '}
                  {t.admin.promotions.used(promotion.stats.redeemed, promotion.maxRedemptions)}
                  {promotion.endsAt
                    ? ` · ${t.admin.promotions.until(formatDate(promotion.endsAt))}`
                    : ''}
                </p>
              </div>
              {promotion.endsAt && new Date(promotion.endsAt).getTime() <= Date.now() ? null : (
                <button
                  type="button"
                  className="kitup-quiet-button"
                  disabled={toggle.isPending}
                  onClick={() => toggle.mutate({ id: promotion.id, active: !promotion.active })}
                >
                  {promotion.active ? t.admin.promotions.pause : t.admin.promotions.resume}
                </button>
              )}
            </div>
            <div className="mt-4 grid gap-2 text-sm sm:grid-cols-3">
              <Metric
                label={t.admin.promotions.redeemed}
                value={promotion.stats.redeemed.toLocaleString(locale)}
              />
              <Metric
                label={t.admin.promotions.revenue}
                value={formatPrice(promotion.stats.revenueMinorUnits, brand, locale)}
              />
              <Metric
                label={t.admin.promotions.discountGiven}
                value={formatPrice(promotion.stats.discountMinorUnits, brand, locale)}
              />
            </div>
            {promotion.note ? <p className="mt-3 text-sm">{promotion.note}</p> : null}
            {promotion.uses.length > 0 ? (
              <details className="mt-4">
                <summary className="cursor-pointer text-sm font-medium">
                  {t.admin.promotions.history}
                </summary>
                <div className="mt-2 overflow-x-auto">
                  <table className="w-full text-left text-sm">
                    <thead>
                      <tr>
                        <th>{t.common.email}</th>
                        <th>{t.admin.promotions.book}</th>
                        <th>{t.admin.promotions.amount}</th>
                        <th>{t.admin.promotions.status}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {promotion.uses.map((use) => (
                        <tr key={use.id}>
                          <td>{use.user.email}</td>
                          <td>{use.book.title}</td>
                          <td>{formatPrice(use.payment.amountMinorUnits, brand, locale)}</td>
                          <td>{t.admin.promotions.useStatuses[use.status]}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </details>
            ) : null}
          </article>
        ))}
        {promotions.data?.items.length === 0 ? <p>{t.admin.promotions.empty}</p> : null}
      </div>
    </main>
  );
}

function Field({
  label,
  children,
}: {
  readonly label: string;
  readonly children: JSX.Element;
}): JSX.Element {
  return (
    <label className="grid gap-1 text-sm">
      <span className="font-medium">{label}</span>
      {children}
    </label>
  );
}

function Metric({ label, value }: { readonly label: string; readonly value: string }): JSX.Element {
  return (
    <div className="rounded-lg bg-[var(--kitup-color-paper)] p-3">
      <span className="text-xs text-[var(--kitup-color-muted)]">{label}</span>
      <p className="font-semibold">{value}</p>
    </div>
  );
}

interface PromotionForm {
  readonly code: string;
  readonly name: string;
  readonly discountPercent: number;
  readonly targetEmail: string;
  readonly firstPurchaseOnly: boolean;
  readonly maxRedemptions: string;
  readonly maxPerUser: string;
  readonly startsAt: string;
  readonly endsAt: string;
  readonly active: boolean;
  readonly note: string;
}

function emptyForm(targetEmail = ''): PromotionForm {
  return {
    code: '',
    name: '',
    discountPercent: 10,
    targetEmail,
    firstPurchaseOnly: false,
    maxRedemptions: '',
    maxPerUser: '1',
    startsAt: localDateTime(new Date()),
    endsAt: '',
    active: true,
    note: '',
  };
}

function toInput(form: PromotionForm): CreateAdminPromotionInput {
  return {
    code: form.code,
    name: form.name,
    discountPercent: form.discountPercent,
    targetEmail: form.targetEmail.trim().toLowerCase() || null,
    firstPurchaseOnly: form.firstPurchaseOnly,
    maxRedemptions: form.maxRedemptions ? Number(form.maxRedemptions) : null,
    maxPerUser: form.maxPerUser ? Number(form.maxPerUser) : null,
    startsAt: new Date(form.startsAt).toISOString(),
    endsAt: form.endsAt ? new Date(form.endsAt).toISOString() : null,
    active: form.active,
    note: form.note.trim() || null,
  };
}

function localDateTime(date: Date): string {
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60_000);
  return local.toISOString().slice(0, 16);
}

function generateCode(): string {
  const alphabet = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  const bytes = crypto.getRandomValues(new Uint8Array(8));
  return Array.from(bytes, (value) => alphabet[value % alphabet.length]).join('');
}

function promotionIsLive(promotion: {
  readonly active: boolean;
  readonly startsAt: string;
  readonly endsAt: string | null;
}): boolean {
  const now = Date.now();
  return (
    promotion.active &&
    new Date(promotion.startsAt).getTime() <= now &&
    (promotion.endsAt === null || new Date(promotion.endsAt).getTime() > now)
  );
}
